const SMS_PROVIDER_STATE = Object.freeze({
  NOT_CONFIGURED: "provider_not_configured",
  PREVIEW_ONLY: "preview_only",
  MISSING_PHONE: "missing_recipient_phone",
});

function normalizeText(value) {
  return String(value || "").trim();
}

function normalizePhone(value) {
  const phone = normalizeText(value);
  return phone || null;
}

function pickFirst(...values) {
  for (const value of values) {
    const normalized = normalizeText(value);
    if (normalized) return normalized;
  }
  return "";
}

function hashToBase36(value) {
  const input = normalizeText(value);
  let hash = 2166136261;
  for (let index = 0; index < input.length; index += 1) {
    hash ^= input.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }
  return (hash >>> 0).toString(36).toUpperCase();
}

export function buildAppointmentReferenceCode(appointmentId) {
  const normalizedId = normalizeText(appointmentId).replace(/[^a-zA-Z0-9]/g, "");
  if (!normalizedId) return "AFM-PREVIEW";

  const code = hashToBase36(normalizedId).padStart(5, "0").slice(-5);
  return `AFM-${code}`;
}

export function formatAppointmentSmsDate(value) {
  const raw = normalizeText(value);
  if (!raw) return "the scheduled time";

  const date = new Date(raw);
  if (Number.isNaN(date.getTime())) return "the scheduled time";

  const pad = (part) => String(part).padStart(2, "0");
  return `${pad(date.getDate())}-${pad(date.getMonth() + 1)}-${date.getFullYear()} at ${pad(
    date.getHours(),
  )}:${pad(date.getMinutes())}`;
}

export function resolveAppointmentSmsRecipient(appointment = {}) {
  return normalizePhone(
    pickFirst(
      appointment.patientPhone,
      appointment.patient_phone,
      appointment.phone,
      appointment.recipientPhone,
      appointment.recipient_phone,
      appointment.patient?.phone,
      appointment.patient?.profile?.phone,
      appointment.patient?.profiles?.phone,
      appointment.patient_profile?.phone,
      appointment.profiles?.phone,
    ),
  );
}

export function buildAppointmentSmsConfirmation({ appointment = {} } = {}) {
  const appointmentId = pickFirst(appointment.id, appointment.appointment_id);
  const referenceCode = buildAppointmentReferenceCode(appointmentId);
  const recipientPhone = resolveAppointmentSmsRecipient(appointment);
  const appointmentDate = pickFirst(
    appointment.appointment_date,
    appointment.appointmentAt,
    appointment.startsAt,
    appointment.starts_at,
    appointment.start,
  );
  const dateText = formatAppointmentSmsDate(appointmentDate);
  const message = `AfriMed™: Your appointment request has been received for ${dateText}. Ref: ${referenceCode}. Keep this code for support.`;
  const missingPhone = !recipientPhone;

  return {
    ok: !missingPhone,
    channel: "sms",
    kind: "appointment_confirmation",
    deliveryStatus: missingPhone ? SMS_PROVIDER_STATE.MISSING_PHONE : SMS_PROVIDER_STATE.PREVIEW_ONLY,
    provider: SMS_PROVIDER_STATE.NOT_CONFIGURED,
    providerMessageId: null,
    recipientPhone,
    referenceCode,
    message,
    canSend: false,
    realSmsSent: false,
    evidencePersisted: false,
    missingPhone,
    sensitiveDataExcluded: true,
    containsClinicalDetails: false,
    security: {
      referenceCodeGrantsAccess: false,
      expiresAt: null,
      note:
        "Slice 1 reference codes are support references only. They do not unlock unauthenticated appointment access.",
    },
    createdAt: new Date().toISOString(),
  };
}

export function queueAppointmentSmsConfirmation(args = {}) {
  const preview = buildAppointmentSmsConfirmation(args);
  return {
    ...preview,
    dispatchState: preview.missingPhone
      ? SMS_PROVIDER_STATE.MISSING_PHONE
      : SMS_PROVIDER_STATE.NOT_CONFIGURED,
    queued: false,
  };
}

export async function sendAppointmentSmsConfirmation(args = {}) {
  const preview = queueAppointmentSmsConfirmation(args);
  return {
    ...preview,
    sent: false,
    skipped: true,
    reason: preview.missingPhone ? SMS_PROVIDER_STATE.MISSING_PHONE : SMS_PROVIDER_STATE.NOT_CONFIGURED,
  };
}

export { SMS_PROVIDER_STATE };
